import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Why Choose Us', href: '/about' },
  {
    label: 'Products', href: '/products',
    children: [
      { label: 'Water Cooled Brine', href: '/products/water-cooled-brine' },
      { label: 'Water Cooled Chilled Water', href: '/products/water-cooled-chilled-water' },
      { label: 'Ammonia Chillers', href: '/products/ammonia' },
      { label: 'Ultra Low Temperature', href: '/products/ultra-low-temp' },
      { label: 'Custom Solutions', href: '/products/custom' },
    ],
  },
  { label: 'News', href: '/news' },
  { label: 'Contact', href: '/contact' },
  { label: 'Selection Tool', href: '/chiller-selector' },
];

export default function Header() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-40 border-b bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70" data-testid="main-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ background: 'var(--brand-teal)' }}>LC</div>
            <div>
              <span className="font-semibold text-base tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Laminate Chillers</span>
              <span className="hidden sm:block text-[10px] text-muted-foreground -mt-1">Industrial Cooling Solutions</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.href} className="relative group"
                onMouseEnter={() => link.children && setDropdownOpen(link.href)}
                onMouseLeave={() => setDropdownOpen(null)}>
                <Link
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
                    isActive(link.href)
                      ? 'text-[var(--brand-teal)]'
                      : 'text-[var(--brand-slate)] hover:text-[var(--brand-navy)]'
                  }`}
                >
                  {link.label}
                  {link.children && <ChevronDown className="w-3 h-3" />}
                </Link>
                {link.children && dropdownOpen === link.href && (
                  <div className="absolute top-full left-0 pt-1 z-50">
                    <div className="bg-white rounded-lg shadow-lg border p-2 min-w-[220px]">
                      {link.children.map((child) => (
                        <Link key={child.href} to={child.href}
                          className="block px-3 py-2 text-sm rounded-md hover:bg-[var(--surface-2)] text-[var(--brand-slate)] hover:text-[var(--brand-navy)] transition-colors"
                          onClick={() => setDropdownOpen(null)}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right section */}
          <div className="flex items-center gap-3">
            <a href="tel:+919030223423" className="hidden md:flex items-center gap-1 text-sm text-muted-foreground hover:text-[var(--brand-navy)]">
              <Phone className="w-3.5 h-3.5" />
              <span>+91 90302 23423</span>
            </a>
            <Link to="/request-quote">
              <Button data-testid="sticky-rfq-cta" className="text-white text-sm" style={{ background: 'var(--brand-teal)' }}>
                Request a Quote
              </Button>
            </Link>
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-8">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ background: 'var(--brand-teal)' }}>LC</div>
                    <span className="font-semibold" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Laminate Chillers</span>
                  </div>
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => (
                      <React.Fragment key={link.href}>
                        <Link to={link.href}
                          onClick={() => setMobileOpen(false)}
                          className={`px-3 py-2.5 text-sm font-medium rounded-md ${
                            isActive(link.href) ? 'bg-[var(--surface-2)] text-[var(--brand-teal)]' : 'text-[var(--brand-slate)]'
                          }`}>
                          {link.label}
                        </Link>
                        {link.children?.map((child) => (
                          <Link key={child.href} to={child.href}
                            onClick={() => setMobileOpen(false)}
                            className="px-6 py-2 text-sm text-muted-foreground hover:text-[var(--brand-navy)]">
                            {child.label}
                          </Link>
                        ))}
                      </React.Fragment>
                    ))}
                  </nav>
                  <div className="mt-8">
                    <Link to="/request-quote" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full text-white" style={{ background: 'var(--brand-teal)' }}>Request a Quote</Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
